// src/widgets/registry.ts
import {
	IconChartPie,
	IconCalendarDue,
	IconFlame,
	IconUsers,
} from '@tabler/icons-react';
import type { WidgetDefinition } from './types';
import QuickStats from './widgets/QuickStats';
import UpcomingBills from './widgets/UpcomingBills';
import PaymentStreak from './widgets/PaymentStreak';
import WhatAreYouPaying, {
	WhatAreYouPayingFormSection,
	validateWhatAreYouPayingData,
} from './widgets/WhatAreYouPaying';

/**
 * Registry of all available widgets.
 * Add new widgets here to make them available in the gallery.
 */
export const widgetRegistry: WidgetDefinition[] = [
	{
		id: 'quick-stats',
		name: 'Quick Stats',
		description: 'Overview of your bills at a glance - total, paid, unpaid, and amount owed.',
		icon: IconChartPie,
		component: QuickStats,
	},
	{
		id: 'upcoming-bills',
		name: 'Upcoming Bills',
		description: 'See bills due in the next 7 days so you never miss a payment.',
		icon: IconCalendarDue,
		component: UpcomingBills,
	},
	{
		id: 'payment-streak',
		name: 'Payment Progress',
		description: 'Track your payment progress with a visual ring showing how many bills are paid.',
		icon: IconFlame,
		component: PaymentStreak,
	},
	{
		id: 'what-are-you-paying',
		name: 'What Are You Paying',
		description: 'See how your bills compare to others in your area anonymously.',
		icon: IconUsers,
		component: WhatAreYouPaying,
		billIntegration: {
			formLabel: 'Share with What Are You Paying',
			formDescription: 'Anonymously compare your bill with others in your area',
			FormSection: WhatAreYouPayingFormSection,
			defaultData: { shared: true, zipCode: '', category: 'other' },
			validateData: validateWhatAreYouPayingData,
		},
	},
];

/**
 * Get a widget definition by its ID
 */
export function getWidgetById(id: string): WidgetDefinition | undefined {
	return widgetRegistry.find((widget) => widget.id === id);
}

/**
 * Get all widgets that have bill integrations
 */
export function getWidgetsWithBillIntegration(): WidgetDefinition[] {
	return widgetRegistry.filter((widget) => widget.billIntegration !== undefined);
}

/**
 * Get enabled widgets that have bill integrations
 */
export function getEnabledWidgetsWithBillIntegration(
	enabledWidgetIds: string[]
): WidgetDefinition[] {
	return widgetRegistry.filter(
		(widget) =>
			widget.billIntegration !== undefined &&
			enabledWidgetIds.includes(widget.id)
	);
}
